// src/theme/index.ts
import { createTheme, ThemeOptions } from "@mui/material/styles";
import type {} from "@mui/material/Typography"; //  <-- keeps tree-shaking happy

import type { CSSProperties } from "react";

/**
 * 1️⃣  Theme keys
 */
declare module "@mui/material/styles" {
  interface TypographyVariants {
    code: CSSProperties;
    smallCode: CSSProperties;
  }
  // allow configuration with `createTheme`
  interface TypographyVariantsOptions {
    code?: CSSProperties;
    smallCode?: CSSProperties;
  }
}

// 1️⃣  Module augmentation for custom variants
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    code: true;
    smallCode: true;
  }
}

/**
 * Raw theme options (typography h6 nudge + custom `code`/`smallCode` variants).
 * Exported so the site can rebuild a per-org theme from the SAME base options
 * via `createTheme({ ...baseThemeOptions, palette: {...} })` — passing `mode`
 * at creation time so MUI derives dark-mode tokens. Building from the already-
 * created light `theme` (the old `createTheme(theme, override)`) deep-merged
 * over light defaults and never derived dark tokens. See siteMuiTheme.ts.
 */
export const baseThemeOptions: ThemeOptions = {
  typography: {
    // default variants (h1–h6, body1, etc.) can be nudged here
    h6: {
      marginBottom: "0.4rem",
    },
  },
  components: {
    MuiTypography: {
      variants: [
        {
          props: { variant: "code" },
          style: {
            fontFamily: "monospace",
            fontSize: "0.7rem",
            lineHeight: "1",
          },
        },
        {
          props: { variant: "smallCode" },
          style: {
            fontFamily: "monospace",
            fontSize: "0.6rem",
            lineHeight: "0.7rem",
          },
        },
      ],
    },
  },
  // palette, spacing, breakpoints … belong here too
};

const theme = createTheme(baseThemeOptions);

export default theme;
